/**
 * The model of the core's generic creation form (Wagtail v2 plan, part 1,
 * step 3, 2026-10-25): the capacity, the high availability and the
 * branches of a new project, read from its type's `AppType` descriptor —
 * its profiles (`spec.profiles`), the roles and editor bounds of its fields,
 * its components, its rules, its branches — with no knowledge of any type.
 * Odoo keeps its own form (`plugins/odoo`, odooProjectForm).
 *
 * THE CHECKS ARE THE ENGINE'S: the values are planned by `planCreation`
 * (`@internal/plugin-waadoo-config-common`), the plan `waadoo:env:configure`
 * runs on a change, here with every field asked (a new environment has no
 * current configuration), within the EDITOR's bounds — those of the
 * creation form; an administrator goes beyond them later, from the
 * « Configuration » tab — and the cluster's ceiling of `quotaProblems`, as
 * `waadoo:env:quota` refuses it at the creation.
 *
 * OUTPUT CONTRACT of `AppCapacityPicker` (what a creation template reads,
 * `AppCapacityValue`):
 *
 *   { appType: string, profile: string, values: { <field id>: value } }
 *
 *  - `appType`: the `AppType` the values are for (`metadata.name`);
 *  - `profile`: the id of one of `spec.profiles`, or `personnalise`
 *    (`CUSTOM_PROFILE`) when the values were set one by one;
 *  - `values`: EVERY configurable field of the descriptor (`configurable`
 *    not false), by id — its `default`, then the profile's values, then the
 *    high availability chosen, then the answers the template asks itself
 *    (below) — as the engine would write them (its adjustments and automatic
 *    fixes applied), each of the field's shape (whole numbers ≥ 0 in the
 *    field's unit, booleans, a PostgreSQL mode, an enum's value). A
 *    non-configurable field (Mailpit's switch) is never in it: the template
 *    asks it itself.
 *
 * A configurable field that neither the capacity nor the high availability
 * shows and that the template asks as a TOP-LEVEL parameter of the same id
 * (Wagtail's `demoData`, on its « Site » step) takes that answer: the
 * picker does not show it and `values` repeats it (`answeredFieldIds`).
 *
 * Refused (`checkCapacity`, in French): another type's values, an unknown
 * profile, a missing or unknown field or a value of the wrong shape, an
 * answer of another step not repeated, values that no longer match their
 * profile, values the engine would change, any refusal of the plan (bounds,
 * constraints, requirements), and an environment beyond the cluster.
 */

import {
  computeQuotaWithDescriptor,
  evaluate,
  formatFieldValue,
  hiddenFieldIds,
  planCreation,
  quotaProblems,
  truthy,
  validateFieldValues,
  valueFits,
  type AppTypeDescriptor,
  type ConfigurationGrant,
  type DescriptorComponent,
  type ExpressionNode,
  type FieldValue,
  type FieldValues,
  type ResourceKey,
  type SingleFieldRole,
} from '@internal/plugin-waadoo-config-common';
import { CUSTOM_PROFILE } from '../projectEnvironments/configuration';

/** The bounds of the creation form: the editors' (header). */
export const CREATION_GRANT: ConfigurationGrant = 'editor';

/** The value of `AppCapacityPicker` (header: the output contract). */
export interface AppCapacityValue {
  appType: string;
  profile: string;
  values: FieldValues;
}

/** A field's own answers, as the form may hold them while being typed. */
export type CapacityDraft = Omit<AppCapacityValue, 'values'> & {
  values: Partial<FieldValues>;
};

/** Whether `record` has its OWN property `key` (never `Object.prototype`'s). */
const hasOwnValue = (record: Readonly<Record<string, unknown>>, key: string) =>
  Object.prototype.hasOwnProperty.call(record, key);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isScalar = (value: unknown): value is FieldValue =>
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  typeof value === 'string';

/** Whether `value` has the contract's shape (its values not checked). */
export function isCapacityDraft(value: unknown): value is CapacityDraft {
  return (
    isRecord(value) &&
    typeof value.appType === 'string' &&
    typeof value.profile === 'string' &&
    isRecord(value.values) &&
    Object.values(value.values).every(isScalar)
  );
}

/** What the creation form reads of a raw `spec` that the engine drops. */
export interface CreationSpec {
  /** `templates.create`, lower-cased as the scaffolder writes a ref. */
  create?: string;
  /** `branches.permanentDefault`. */
  permanentDefault?: string[];
  /** `branches.ephemeral.enabled`. */
  ephemeral: boolean;
  /** `fields[].help`, by field id. */
  help: Record<string, string>;
}

export function creationSpecOf(spec: unknown): CreationSpec {
  const templates = isRecord(spec) ? spec.templates : undefined;
  const branches = isRecord(spec) ? spec.branches : undefined;
  const create = isRecord(templates) ? templates.create : undefined;
  const permanent = isRecord(branches) ? branches.permanentDefault : undefined;
  const ephemeral = isRecord(branches) ? branches.ephemeral : undefined;
  const fields =
    isRecord(spec) && Array.isArray(spec.fields) ? spec.fields : [];
  const help: Record<string, string> = {};
  for (const field of fields) {
    if (
      isRecord(field) &&
      typeof field.id === 'string' &&
      typeof field.help === 'string'
    ) {
      help[field.id] = field.help;
    }
  }
  return {
    ...(typeof create === 'string' ? { create: create.toLowerCase() } : {}),
    ...(Array.isArray(permanent) &&
    permanent.every(item => typeof item === 'string')
      ? { permanentDefault: permanent as string[] }
      : {}),
    ephemeral: isRecord(ephemeral) && ephemeral.enabled === true,
    help,
  };
}

// --- Where each field is shown ----------------------------------------------

type RoleFields = Partial<Record<SingleFieldRole, string>>;

/** One card of the high availability: a component and its fields by role. */
export interface HaPart {
  component: DescriptorComponent;
  roles: RoleFields;
}

/** The roles each card of the high availability shows, in its order. */
const APP_HA_ROLES: readonly SingleFieldRole[] = [
  'enabled',
  'replicas',
  'hpaEnabled',
  'hpaMin',
  'hpaMax',
  'hpaTargetCpu',
  'hpaScaleDownWindow',
];
const DATABASE_HA_ROLES: readonly SingleFieldRole[] = [
  'enabled',
  'databaseMode',
  'databaseSync',
];
const CACHE_HA_ROLES: readonly SingleFieldRole[] = ['enabled'];

export interface HaLayout {
  /** One card per application component with such a field. */
  apps: HaPart[];
  /** The CloudNativePG component, with such a field. */
  database?: HaPart;
  /** Each Valkey component's switch: in the first application's card. */
  caches: HaPart[];
}

/** The fields of `roles` among `order` that are configurable. */
function pick(
  descriptor: AppTypeDescriptor,
  component: DescriptorComponent,
  order: readonly SingleFieldRole[],
): RoleFields {
  const all = descriptor.componentRoles.get(component.id)?.roles ?? {};
  const picked: RoleFields = {};
  for (const role of order) {
    const id = all[role];
    if (id !== undefined && descriptor.fieldById.get(id)?.configurable) {
      picked[role] = id;
    }
  }
  return picked;
}

const hasFields = (part: HaPart) => Object.keys(part.roles).length > 0;

/** The cards of the high availability (`HaLayout`). */
export function haLayout(descriptor: AppTypeDescriptor): HaLayout {
  const apps = descriptor.components
    .filter(component => component.kind === 'app')
    .map(component => ({
      component,
      roles: pick(descriptor, component, APP_HA_ROLES),
    }))
    .filter(hasFields);
  const database = descriptor.databaseComponent && {
    component: descriptor.databaseComponent,
    roles: pick(descriptor, descriptor.databaseComponent, DATABASE_HA_ROLES),
  };
  const caches = descriptor.components
    .filter(component => component.kind === 'valkey')
    .map(component => ({
      component,
      roles: pick(descriptor, component, CACHE_HA_ROLES),
    }))
    .filter(hasFields);
  return {
    // Without an application card, a cache's switch has nowhere to go: it
    // is then « Personnalisé »'s, as any field the cards do not show.
    apps,
    ...(database && hasFields(database) ? { database } : {}),
    caches: apps.length > 0 ? caches : [],
  };
}

/** Every field the high availability shows. */
export function haFieldIds(descriptor: AppTypeDescriptor): Set<string> {
  const { apps, database, caches } = haLayout(descriptor);
  return new Set(
    [...apps, ...(database ? [database] : []), ...caches].flatMap(part =>
      Object.values(part.roles).filter((id): id is string => id !== undefined),
    ),
  );
}

/** Every field one of the profiles sets. */
export function profileFieldIds(descriptor: AppTypeDescriptor): Set<string> {
  return new Set(
    descriptor.profiles.flatMap(profile => Object.keys(profile.values)),
  );
}

/**
 * The configurable fields the template asks itself (header): neither shown
 * by the high availability nor set by a profile, and answered by a
 * top-level parameter of the same id with a value of the field's shape.
 */
export function answeredFieldIds(
  descriptor: AppTypeDescriptor,
  answers: Readonly<Record<string, unknown>>,
): Set<string> {
  const ha = haFieldIds(descriptor);
  const profiles = profileFieldIds(descriptor);
  return new Set(
    descriptor.configurableFields
      .filter(
        field =>
          !ha.has(field.id) &&
          !profiles.has(field.id) &&
          hasOwnValue(answers, field.id) &&
          valueFits(field, answers[field.id]),
      )
      .map(field => field.id),
  );
}

/**
 * The fields whose `when` is false for the project being created
 * (`hiddenFieldIds`), over the template's answers to the non-configurable
 * fields (`plainInputs`: Wagtail's `mediaS3`) and `values`: not shown, not
 * counted in the disk. Their values stay in the output (the contract: every
 * configurable field), written to the environments' `values.yaml` — the
 * branches `dev-*` read dev's under the light version, which may show them.
 */
export function formHiddenFields(
  descriptor: AppTypeDescriptor,
  answers: Readonly<Record<string, unknown>>,
  values: Partial<FieldValues>,
): Set<string> {
  return hiddenFieldIds(descriptor, {
    ...plainInputs(descriptor, answers),
    ...values,
  });
}

/**
 * What « Personnalisé » shows, by component in the descriptor's order:
 * every configurable field neither the high availability shows nor the
 * template asks itself, nor hidden by its `when` (`formHiddenFields`).
 */
export function customFields(
  descriptor: AppTypeDescriptor,
  answers: Readonly<Record<string, unknown>>,
  values: Partial<FieldValues>,
): Array<{ component: DescriptorComponent; fields: string[] }> {
  const ha = haFieldIds(descriptor);
  const answered = answeredFieldIds(descriptor, answers);
  const hidden = formHiddenFields(descriptor, answers, values);
  return descriptor.components
    .map(component => ({
      component,
      fields: descriptor.configurableFields
        .filter(
          field =>
            field.component === component.id &&
            !ha.has(field.id) &&
            !answered.has(field.id) &&
            !hidden.has(field.id),
        )
        .map(field => field.id),
    }))
    .filter(group => group.fields.length > 0);
}

// --- The value --------------------------------------------------------------

/** The profile a creation form picks first (`spec.profiles[].default`). */
export function defaultProfileId(descriptor: AppTypeDescriptor): string {
  return (
    descriptor.profiles.find(profile => profile.default)?.id ??
    descriptor.profiles[0]?.id ??
    CUSTOM_PROFILE
  );
}

/** Whether every configurable field has a value of its shape. */
export function isComplete(
  descriptor: AppTypeDescriptor,
  values: Partial<FieldValues>,
): values is FieldValues {
  return descriptor.configurableFields.every(field =>
    valueFits(field, values[field.id]),
  );
}

/**
 * The values as the engine would write them (`planCreation`: its
 * adjustments and automatic fixes), once complete; as they are until then.
 */
export function settle(
  descriptor: AppTypeDescriptor,
  values: Partial<FieldValues>,
): Partial<FieldValues> {
  if (!isComplete(descriptor, values)) return values;
  return planCreation({ descriptor, values, grant: CREATION_GRANT }).desired;
}

function withAnswers(
  descriptor: AppTypeDescriptor,
  values: Partial<FieldValues>,
  answers: Readonly<Record<string, unknown>>,
): Partial<FieldValues> {
  const next = { ...values };
  for (const id of answeredFieldIds(descriptor, answers)) {
    next[id] = answers[id] as FieldValue;
  }
  return next;
}

/**
 * The first value of the field: every field's default, the default
 * profile's values, the template's own answers.
 */
export function initialCapacity(
  descriptor: AppTypeDescriptor,
  answers: Readonly<Record<string, unknown>>,
): CapacityDraft {
  const values: Partial<FieldValues> = {};
  for (const field of descriptor.configurableFields) {
    if (field.default !== undefined) values[field.id] = field.default;
  }
  const profile = defaultProfileId(descriptor);
  Object.assign(
    values,
    descriptor.profiles.find(item => item.id === profile)?.values,
  );
  return withMatchingProfile(descriptor, {
    appType: descriptor.name,
    profile,
    values: settle(descriptor, withAnswers(descriptor, values, answers)),
  });
}

/** The draft with the values of the profile `profile` (or « Personnalisé »). */
export function withProfile(
  descriptor: AppTypeDescriptor,
  draft: CapacityDraft,
  profile: string,
): CapacityDraft {
  const values = descriptor.profiles.find(item => item.id === profile)?.values;
  return withMatchingProfile(descriptor, {
    ...draft,
    profile,
    values: values
      ? settle(descriptor, { ...draft.values, ...values })
      : draft.values,
  });
}

/**
 * `draft` under « Personnalisé » when its values no longer hold its
 * profile's — a rule of the type (an adjustment, an automatic fix) changed
 * one of them while settling —, as it is otherwise. The values are what the
 * engine would write, so they are kept and the profile follows them:
 * keeping the profile would leave a refusal (« ne correspondent plus au
 * profil ») that choosing the profile again could not lift.
 */
export function withMatchingProfile(
  descriptor: AppTypeDescriptor,
  draft: CapacityDraft,
): CapacityDraft {
  const profile = descriptor.profiles.find(item => item.id === draft.profile);
  if (
    !profile ||
    !isComplete(descriptor, draft.values) ||
    Object.entries(profile.values).every(
      ([id, value]) => draft.values[id] === value,
    )
  ) {
    return draft;
  }
  return { ...draft, profile: CUSTOM_PROFILE };
}

/**
 * The draft with `id` set to `value` (undefined: emptied, while typed). A
 * field a profile sets turns the profile into « Personnalisé ».
 */
export function withField(
  descriptor: AppTypeDescriptor,
  draft: CapacityDraft,
  id: string,
  value: FieldValue | undefined,
): CapacityDraft {
  const values = { ...draft.values };
  if (value === undefined) {
    delete values[id];
  } else {
    values[id] = value;
  }
  return withMatchingProfile(descriptor, {
    ...draft,
    profile: profileFieldIds(descriptor).has(id)
      ? CUSTOM_PROFILE
      : draft.profile,
    values: settle(descriptor, values),
  });
}

/**
 * The draft with the template's own answers repeated (header), or the same
 * draft when they already are: the field calls it when it is shown, an
 * answer of an earlier step may have changed meanwhile.
 */
export function withCurrentAnswers(
  descriptor: AppTypeDescriptor,
  draft: CapacityDraft,
  answers: Readonly<Record<string, unknown>>,
): CapacityDraft {
  const next = withMatchingProfile(descriptor, {
    ...draft,
    values: settle(descriptor, withAnswers(descriptor, draft.values, answers)),
  });
  return sameDraft(next, draft) ? draft : next;
}

/**
 * Whether two drafts hold the same answers — type, profile, every value —
 * whatever their identity: an answer a rule of the type overrides (an
 * automatic fix on `demoData`) gives a new, equal object on every call.
 */
export function sameDraft(a: CapacityDraft, b: CapacityDraft): boolean {
  const keys = Object.keys(a.values);
  return (
    a.appType === b.appType &&
    a.profile === b.profile &&
    keys.length === Object.keys(b.values).length &&
    keys.every(
      id =>
        Object.prototype.hasOwnProperty.call(b.values, id) &&
        a.values[id] === b.values[id],
    )
  );
}

// --- The refusals -----------------------------------------------------------

/** One refusal, in French, with the fields it concerns (maybe none). */
export interface CapacityProblem {
  text: string;
  fields: string[];
}

const problem = (text: string, fields: string[] = []): CapacityProblem => ({
  text,
  fields,
});

/**
 * The values of the non-configurable fields, for the quota (Mailpit): the
 * template's answer of the same id, else the field's default.
 */
export function plainInputs(
  descriptor: AppTypeDescriptor,
  answers: Readonly<Record<string, unknown>>,
): FieldValues {
  const values: FieldValues = {};
  for (const field of descriptor.fields.filter(item => !item.configurable)) {
    const answer = hasOwnValue(answers, field.id)
      ? answers[field.id]
      : undefined;
    if (valueFits(field, answer)) {
      values[field.id] = answer;
    } else if (field.default !== undefined) {
      values[field.id] = field.default;
    }
  }
  return values;
}

/** Why `draft` cannot be created (header), in French; empty when it can. */
export function checkCapacity(
  descriptor: AppTypeDescriptor,
  draft: CapacityDraft,
  answers: Readonly<Record<string, unknown>>,
): CapacityProblem[] {
  if (draft.appType !== descriptor.name) {
    return [
      problem(
        `La capacité a été choisie pour le type « ${draft.appType} », pas pour ${descriptor.title} : choisissez-la à nouveau.`,
      ),
    ];
  }
  const problems: CapacityProblem[] = [];
  const profile = descriptor.profiles.find(item => item.id === draft.profile);
  if (!profile && draft.profile !== CUSTOM_PROFILE) {
    problems.push(problem(`Profil inconnu : « ${draft.profile} ».`));
  }
  for (const field of descriptor.configurableFields) {
    const value = draft.values[field.id];
    if (value === undefined) {
      problems.push(problem(`${field.label} : valeur manquante.`, [field.id]));
    } else if (!valueFits(field, value)) {
      problems.push(
        problem(`${field.label} : valeur refusée (${String(value)}).`, [
          field.id,
        ]),
      );
    }
  }
  const known = new Set(descriptor.configurableFields.map(field => field.id));
  const unknown = Object.keys(draft.values).filter(id => !known.has(id));
  if (unknown.length > 0) {
    try {
      validateFieldValues(descriptor, draft.values, {
        configurableOnly: true,
        requireAll: false,
      });
    } catch (error: unknown) {
      problems.push(
        problem(error instanceof Error ? error.message : String(error)),
      );
    }
  }
  if (problems.length > 0 || !isComplete(descriptor, draft.values)) {
    return problems;
  }
  const values = draft.values;

  // The answers as the engine writes them: a rule of the type may override
  // one (an automatic fix), which is then no stale answer.
  const answered = settle(descriptor, withAnswers(descriptor, values, answers));
  for (const id of answeredFieldIds(descriptor, answers)) {
    if (values[id] !== answered[id]) {
      const field = descriptor.fieldById.get(id);
      problems.push(
        problem(
          `${field?.label ?? id} a changé à une autre étape (${
            field ? formatFieldValue(field, answers[id] as FieldValue) : ''
          }) : cette étape la reprend, vérifiez-la puis continuez.`,
          [id],
        ),
      );
    }
  }
  if (profile) {
    const differ = Object.entries(profile.values)
      .filter(([id, value]) => values[id] !== value)
      .map(([id]) => id);
    if (differ.length > 0) {
      problems.push(
        problem(
          `Les valeurs ne correspondent plus au profil « ${profile.label} » : choisissez-le à nouveau, ou « Personnalisé ».`,
          differ,
        ),
      );
    }
  }

  const plan = planCreation({ descriptor, values, grant: CREATION_GRANT });
  for (const detail of plan.problemDetails) {
    problems.push(
      problem(
        detail.text,
        detail.field !== undefined ? [detail.field] : detail.fields ?? [],
      ),
    );
  }
  for (const change of plan.changes) {
    if (change.after !== values[change.field]) {
      const field = descriptor.fieldById.get(change.field);
      problems.push(
        problem(
          `${field?.label ?? change.field} : ${
            field ? formatFieldValue(field, change.before) : ''
          } serait remplacé par ${
            field ? formatFieldValue(field, change.after) : ''
          } (règles du type) : choisissez à nouveau.`,
          [change.field],
        ),
      );
    }
  }
  try {
    const quota = computeQuotaWithDescriptor(
      descriptor,
      {
        configuration: plan.desired,
        inputs: { values: plainInputs(descriptor, answers), extras: {} },
      },
      'permanent',
    );
    problems.push(...quotaProblems(quota).map(text => problem(text)));
  } catch (error: unknown) {
    problems.push(
      problem(error instanceof Error ? error.message : String(error)),
    );
  }
  return problems;
}

// --- The figures ------------------------------------------------------------

/** Millicores, Mi, and Go of disk. */
export interface Totals {
  cpuRequest: number;
  cpuLimit: number;
  memoryRequest: number;
  memoryLimit: number;
  disk: number;
}

/** One running component of an environment. */
export interface ComponentLoad {
  component: DescriptorComponent;
  /** At rest (the autoscaling's minimum) and at the peak (`pods`). */
  pods: { min: number; max: number };
  /** One pod's resources; 0 for a resource it declares none of. */
  pod: Record<ResourceKey, number>;
}

/** What one permanent environment runs, at rest and at its peak. */
export interface EnvironmentFigures {
  components: ComponentLoad[];
  rest: Totals;
  peak: Totals;
  /** Whether the autoscaling may add pods. */
  scales: boolean;
}

const RESOURCES: readonly ResourceKey[] = [
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
];

const wholeNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

function podOf(
  component: DescriptorComponent,
  values: FieldValues,
): Record<ResourceKey, number> | undefined {
  const pod: Partial<Record<ResourceKey, number>> = {};
  for (const key of RESOURCES) {
    const ref = component.resources[key];
    const figure = typeof ref === 'string' ? values[ref] : ref ?? 0;
    if (!wholeNumber(figure)) return undefined;
    pod[key] = figure;
  }
  return pod as Record<ResourceKey, number>;
}

/**
 * The disk of an environment: each volume once (one volume shared by the
 * pods that mount it), each database's volume once per instance; a field
 * of `hidden` (its `when` false: Wagtail's media on S3) not at all.
 */
function diskOf(
  descriptor: AppTypeDescriptor,
  values: FieldValues,
  instances: (component: DescriptorComponent) => number,
  hidden: ReadonlySet<string>,
): number {
  let disk = 0;
  const counted = new Set<string>();
  for (const component of descriptor.components) {
    const roles = descriptor.componentRoles.get(component.id);
    for (const id of roles?.volumes ?? []) {
      const size = values[id];
      if (!counted.has(id) && !hidden.has(id) && typeof size === 'number') {
        counted.add(id);
        disk += size;
      }
    }
    const database = roles?.roles.databaseSize;
    const size = database === undefined ? undefined : values[database];
    if (
      database !== undefined &&
      !hidden.has(database) &&
      typeof size === 'number'
    ) {
      disk += size * instances(component);
    }
  }
  return disk;
}

const zero = (): Totals => ({
  cpuRequest: 0,
  cpuLimit: 0,
  memoryRequest: 0,
  memoryLimit: 0,
  disk: 0,
});

/**
 * The figures of one permanent environment over `values` (header of
 * `EnvironmentFigures`): every component its `enabled` keeps, at its `pods`
 * — the descriptor's count « au pire », the autoscaling's maximum — and at
 * rest with the autoscaling's minimum; Mailpit, the Jobs and the platform's
 * own pods are the quota's, not counted. `inputs`: the non-configurable
 * fields (`plainInputs`), which the expressions and the `when`s may read.
 * Undefined while a value is missing.
 */
export function environmentFigures(
  descriptor: AppTypeDescriptor,
  values: Partial<FieldValues>,
  inputs: FieldValues = {},
): EnvironmentFigures | undefined {
  if (!isComplete(descriptor, values)) return undefined;
  const context = { values: { ...inputs, ...values } };
  const hidden = hiddenFieldIds(descriptor, context.values);
  /** An expression of the descriptor over `values`; undefined on a failure. */
  const read = (node: ExpressionNode) => {
    try {
      return evaluate(node, context);
    } catch {
      // A field the expression reads that the configuration does not hold
      // (a non-configurable one): no figure rather than a wrong one.
      return undefined;
    }
  };
  const components: ComponentLoad[] = [];
  for (const component of descriptor.components) {
    if (component.enabled) {
      const enabled = read(component.enabled);
      if (enabled === undefined) return undefined;
      if (!truthy(enabled)) continue;
    }
    const max = read(component.pods);
    const pod = podOf(component, values);
    if (!wholeNumber(max) || !pod) return undefined;
    const roles = descriptor.componentRoles.get(component.id)?.roles ?? {};
    const hpaMin =
      roles.hpaMin === undefined ? undefined : values[roles.hpaMin];
    const scaling =
      roles.hpaEnabled !== undefined && values[roles.hpaEnabled] === true;
    const min = scaling && wholeNumber(hpaMin) ? Math.min(hpaMin, max) : max;
    components.push({ component, pods: { min, max }, pod });
  }
  const total = (side: 'min' | 'max'): Totals => {
    const sum = zero();
    for (const load of components) {
      for (const key of RESOURCES) {
        sum[key] += load.pod[key] * load.pods[side];
      }
    }
    sum.disk = diskOf(
      descriptor,
      values,
      component =>
        components.find(load => load.component === component)?.pods.max ?? 0,
      hidden,
    );
    return sum;
  };
  return {
    components,
    rest: total('min'),
    peak: total('max'),
    scales: components.some(load => load.pods.max > load.pods.min),
  };
}

/**
 * What a profile card says: ONE pod of each component whose resources the
 * configuration sizes (a field gives them), each volume once — as the Odoo
 * form's cards and `sizedReservation` count a profile; a volume hidden by
 * its `when` over `inputs` and `values` not counted. Undefined while a
 * value is missing.
 */
export function profileTotals(
  descriptor: AppTypeDescriptor,
  values: Partial<FieldValues>,
  inputs: FieldValues = {},
): Totals | undefined {
  if (!isComplete(descriptor, values)) return undefined;
  const sum = zero();
  for (const component of descriptor.components) {
    const sized = RESOURCES.some(
      key => typeof component.resources[key] === 'string',
    );
    const pod = podOf(component, values);
    if (!pod) return undefined;
    if (!sized) continue;
    for (const key of RESOURCES) {
      if (typeof component.resources[key] === 'string') sum[key] += pod[key];
    }
  }
  sum.disk = diskOf(
    descriptor,
    values,
    () => 1,
    hiddenFieldIds(descriptor, { ...inputs, ...values }),
  );
  return sum;
}

/**
 * The estimate of the database connections, when the descriptor allows it:
 * a rule of id `connections` whose test compares what may be opened (left)
 * with the limit (right), `<=` or `<`. Undefined otherwise.
 */
export function connectionsEstimate(
  descriptor: AppTypeDescriptor,
  values: Partial<FieldValues>,
): { used: number; limit: number } | undefined {
  if (!isComplete(descriptor, values)) return undefined;
  const rule = descriptor.rules.find(
    item => item.kind === 'check' && item.id === 'connections',
  );
  if (rule?.kind !== 'check') return undefined;
  const { test } = rule;
  if (test.kind !== 'binary' || (test.op !== '<=' && test.op !== '<')) {
    return undefined;
  }
  try {
    const used = evaluate(test.left, { values });
    const limit = evaluate(test.right, { values });
    return typeof used === 'number' && typeof limit === 'number'
      ? { used, limit }
      : undefined;
  } catch {
    // A side reading a field the configuration does not hold: no estimate.
    return undefined;
  }
}
